import moment from 'moment';
import { readPersistedSessionIds } from './kronosPrefs/sessionIds';
import {
    emptyUserInstancePrefs,
    readUserInstancePrefsDoc,
    writeUserInstancePrefsDoc,
} from './kronosPrefs/userInstancePrefs';
import { pickDefaultProgrammationPlaceId } from './programmationPlacePrefs';
import {
    readLastProgrammationPricinglistId,
    readLastProgrammationBucketlistId,
} from './programmationResourcePrefs';
import { pickDefaultResourceId } from './resourceDefaults';
import { empty_group_options } from './programmationGroupDefaults';
import Utils from './Utils';

/**
 * Last programmation "details" field values per user + instance (including empty values).
 */
export function readProgrammationCreateDefaults(instanceId) {
    const { userId, instanceId: instFromSession } = readPersistedSessionIds();
    if (!userId) {
        return null;
    }
    const inst = instanceId != null ? String(instanceId) : instFromSession;
    const doc = readUserInstancePrefsDoc(userId, inst) || emptyUserInstancePrefs();
    const stored = doc.programmations?.createDefaults;
    return stored && typeof stored === 'object' ? stored : null;
}

/**
 * @param {object} defaults
 * @param {string|null|undefined} [instanceId]
 */
export function writeProgrammationCreateDefaults(defaults, instanceId) {
    const { userId, instanceId: instFromSession } = readPersistedSessionIds();
    if (!userId) {
        return;
    }
    const inst = instanceId != null ? String(instanceId) : instFromSession;
    const doc = readUserInstancePrefsDoc(userId, inst) || emptyUserInstancePrefs();
    doc.programmations = {
        ...(doc.programmations || {}),
        createDefaults: defaults,
    };
    writeUserInstancePrefsDoc(userId, inst, doc);
}

/**
 * @param {Array<object>|undefined|null} tasks
 * @returns {Array<{ activity_id: string|null, user_id: string|null, status: string|null }>}
 */
export function normalize_tasks_for_prefs(tasks) {
    if (!Array.isArray(tasks)) {
        return [];
    }
    return tasks.map((t) => ({
        activity_id: t?.activity_id ?? t?.activity?.id ?? null,
        user_id: t?.user_id ?? t?.user?.id ?? null,
        status: t?.status ?? null,
    }));
}

/**
 * @param {Array<{ activity_id?: string|null, user_id?: string|null, status?: string|null }>|undefined|null} stored
 * @param {Array<{ id: string }>|undefined|null} activities
 * @param {Array<{ id: string }>|undefined|null} users
 * @returns {Array<object>}
 */
export function hydrate_tasks_from_prefs(stored, activities, users) {
    if (!Array.isArray(stored) || !stored.length) {
        return [];
    }
    const activity_list = activities || [];
    const user_list = users || [];
    return stored
        .map((st) => {
            const activity_id = st?.activity_id;
            if (activity_id == null || activity_id === '') {
                return null;
            }
            const activity = activity_list.find((a) => String(a.id) === String(activity_id));
            if (!activity) {
                return null;
            }
            const task = {
                activity_id: String(activity_id),
                activity,
                status: st?.status ?? null,
            };
            const user_id = st?.user_id;
            if (user_id != null && user_id !== '') {
                const user = user_list.find((u) => String(u.id) === String(user_id));
                if (user) {
                    task.user_id = String(user_id);
                    task.user = user;
                }
            }
            return task;
        })
        .filter(Boolean);
}

/**
 * @param {moment.Moment|Date|string} start_at
 * @param {number|string} period_nb
 * @param {string} period_type
 * @returns {moment.Moment}
 */
export function compute_programmation_stop_at(start_at, period_nb, period_type) {
    const stop_at = moment(start_at).clone().endOf('day');
    const nb = Number(period_nb);
    const count = Number.isFinite(nb) && nb > 0 ? nb : 1;
    switch (period_type) {
        case Utils.PERIOD_DAY:
            return stop_at.add(count - 1, 'days');
        case Utils.PERIOD_WEEK:
        case Utils.PERIOD_CINEMA_WEEK:
            return stop_at.add(7 * count - 1, 'days');
        case Utils.PERIOD_MONTH:
            return stop_at.endOf('month');
        case Utils.PERIOD_YEAR:
            return stop_at.endOf('year');
        default:
            return stop_at.add(7 * count - 1, 'days');
    }
}

/**
 * @param {object} values programmation form values
 * @returns {object}
 */
export function snapshot_programmation_create_defaults(values) {
    const opts = values?.options || {};
    const period_nb_raw = values?.period_nb;
    const period_nb = (period_nb_raw === '' || period_nb_raw == null)
        ? 1
        : period_nb_raw;
    return {
        week: (opts.week === '' || opts.week == null) ? 1 : opts.week,
        sections: opts.sections ?? [],
        _3d: opts._3d ?? false,
        break: opts.break ?? false,
        version: opts.version ?? '',
        format: opts.format ?? '',
        support: opts.support ?? '',
        ignore_on_maccsbox: opts.ignore_on_maccsbox ?? false,
        booking_mode: opts.booking_mode ?? '',
        booking_note: opts.booking_note && typeof opts.booking_note === 'object'
            ? { ...opts.booking_note }
            : {},
        period_nb,
        period_type: values?.period_type || Utils.PERIOD_WEEK,
        tasks: normalize_tasks_for_prefs(values?.tasks),
    };
}

/**
 * @param {object} base programmation draft from redux
 * @param {string|null|undefined} [instanceId]
 * @param {{ activities?: Array<object>, users?: Array<object> }} [linked]
 * @returns {object}
 */
export function apply_programmation_create_defaults(base, instanceId, linked = {}) {
    const stored = readProgrammationCreateDefaults(instanceId);
    if (!stored) {
        return base;
    }
    const options = { ...(base.options || {}) };
    if (Object.prototype.hasOwnProperty.call(stored, 'week')) {
        const week = stored.week;
        options.week = week === '' || week == null ? 1 : week;
    }
    if (Object.prototype.hasOwnProperty.call(stored, 'sections')) {
        options.sections = stored.sections;
    }
    if (Object.prototype.hasOwnProperty.call(stored, '_3d')) {
        options._3d = stored._3d;
    }
    if (Object.prototype.hasOwnProperty.call(stored, 'break')) {
        options.break = stored.break;
    }
    if (Object.prototype.hasOwnProperty.call(stored, 'version')) {
        options.version = stored.version;
    }
    if (Object.prototype.hasOwnProperty.call(stored, 'format')) {
        options.format = stored.format;
    }
    if (Object.prototype.hasOwnProperty.call(stored, 'support')) {
        options.support = stored.support;
    }
    if (Object.prototype.hasOwnProperty.call(stored, 'ignore_on_maccsbox')) {
        options.ignore_on_maccsbox = stored.ignore_on_maccsbox;
    }
    if (Object.prototype.hasOwnProperty.call(stored, 'booking_mode')) {
        options.booking_mode = stored.booking_mode ?? '';
    }
    if (Object.prototype.hasOwnProperty.call(stored, 'booking_note')) {
        options.booking_note = stored.booking_note && typeof stored.booking_note === 'object'
            ? { ...stored.booking_note }
            : {};
    }
    const next = {
        ...base,
        options,
    };
    let period_changed = false;
    if (Object.prototype.hasOwnProperty.call(stored, 'period_nb')) {
        const period_nb = stored.period_nb;
        next.period_nb = period_nb === '' || period_nb == null ? 1 : period_nb;
        period_changed = true;
    }
    if (Object.prototype.hasOwnProperty.call(stored, 'period_type')) {
        next.period_type = stored.period_type || Utils.PERIOD_WEEK;
        period_changed = true;
    }
    if (next.period_type === Utils.PERIOD_CINEMA_WEEK || period_changed) {
        if (next.period_type === Utils.PERIOD_CINEMA_WEEK) {
            next.start_at = Utils.snap_to_next_wednesday(
                next.start_at || base.start_at || moment().startOf('day')
            );
        }
        next.stop_at = compute_programmation_stop_at(
            next.start_at || base.start_at || moment().startOf('day'),
            next.period_nb,
            next.period_type
        );
    }
    if (Object.prototype.hasOwnProperty.call(stored, 'tasks')) {
        next.tasks = hydrate_tasks_from_prefs(
            stored.tasks,
            linked.activities,
            linked.users
        );
    }
    return next;
}

/**
 * @param {object|null|undefined} programmation
 * @param {string|null|undefined} [instanceId]
 */
export function rememberProgrammationCreateDefaultsFromProgrammation(programmation, instanceId) {
    if (!programmation) {
        return;
    }
    writeProgrammationCreateDefaults(
        snapshot_programmation_create_defaults(programmation),
        instanceId
    );
}

/**
 * Persist defaults from the last programmation row in a group (or list).
 *
 * @param {Array<object>|undefined|null} programmations
 * @param {string|null|undefined} [instanceId]
 */
export function rememberProgrammationCreateDefaultsFromProgrammationsList(programmations, instanceId) {
    if (!Array.isArray(programmations) || programmations.length === 0) {
        writeProgrammationCreateDefaults(
            snapshot_programmation_create_defaults({ options: {}, tasks: [] }),
            instanceId
        );
        return;
    }
    const last = programmations[programmations.length - 1];
    rememberProgrammationCreateDefaultsFromProgrammation(last, instanceId);
}

/**
 * Draft for a new programmation row (standalone create or group "add").
 *
 * @param {{
 *   places?: Array<object>,
 *   pricinglists?: Array<object>,
 *   bucketlists?: Array<object>,
 *   activities?: Array<object>,
 *   users?: Array<object>,
 *   instanceId?: string|null,
 * }} params
 * @returns {object}
 */
export function build_new_programmation_draft({
    places,
    pricinglists,
    bucketlists,
    activities,
    users,
    instanceId,
}) {
    const start_at = moment().startOf('day');
    const base = {
        days: [],
        times: [],
        period_nb: 1,
        period_type: Utils.PERIOD_WEEK,
        events: [],
        tasks: [],
        start_at,
        stop_at: compute_programmation_stop_at(start_at, 1, Utils.PERIOD_WEEK),
        description: {},
        place_id: pickDefaultProgrammationPlaceId({
            places: places || [],
            programmationPlaceId: null,
            singlePlaceFromStoreId: null,
        }),
        pricinglist_id: pickDefaultResourceId({
            items: pricinglists,
            currentId: null,
            lastStoredId: readLastProgrammationPricinglistId(instanceId),
        }),
        bucketlist_id: pickDefaultResourceId({
            items: bucketlists,
            currentId: null,
            lastStoredId: readLastProgrammationBucketlistId(instanceId),
        }),
        options: {
            week: 1,
            sections: [],
            _3d: false,
            break: false,
            version: '',
            format: '',
            support: '',
            ignore_on_maccsbox: false,
        },
    };
    return apply_programmation_create_defaults(base, instanceId, { activities, users });
}

/**
 * Draft for a new programmation group (create form).
 *
 * @param {{
 *   pricinglists?: Array<object>,
 *   bucketlists?: Array<object>,
 *   activities?: Array<object>,
 *   users?: Array<object>,
 *   instanceId?: string|null,
 * }} [params]
 * @returns {object}
 */
export function build_new_programmation_group_draft({
    pricinglists,
    bucketlists,
    activities,
    users,
    instanceId,
} = {}) {
    const start_at = moment().startOf('day');
    const base = {
        period_nb: 1,
        period_type: Utils.PERIOD_WEEK,
        // One empty row so /new shows an event select without an extra click.
        events: [{
            id: '',
            event_id: '',
            title: {},
            audio: '',
            subtitles: [],
        }],
        // Empty array (not omitted): validators must not fall through to
        // root place/days/times as if this were a standalone programmation.
        programmations: [],
        tasks: [],
        title: {},
        description: {},
        options: empty_group_options(),
        start_at,
        stop_at: compute_programmation_stop_at(start_at, 1, Utils.PERIOD_WEEK),
        pricinglist_id: pickDefaultResourceId({
            items: pricinglists,
            currentId: null,
            lastStoredId: readLastProgrammationPricinglistId(instanceId),
        }),
        bucketlist_id: pickDefaultResourceId({
            items: bucketlists,
            currentId: null,
            lastStoredId: readLastProgrammationBucketlistId(instanceId),
        }),
    };
    const with_prefs = apply_programmation_create_defaults(base, instanceId, {
        activities,
        users,
    });
    // Group options do not use "week" (slot-level only).
    if (with_prefs.options && Object.prototype.hasOwnProperty.call(with_prefs.options, 'week')) {
        const { week: _week, ...group_options } = with_prefs.options;
        with_prefs.options = group_options;
    }
    return with_prefs;
}
